import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Handshake, AlertCircle, CheckCircle2, XCircle, Calendar } from "lucide-react";

// Cores por status do acordo
const STATUS_COLOR = {
  ATIVO: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  QUITADO: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  INADIMPLENTE: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  CANCELADO: "bg-red-500/10 text-red-600 border-red-500/30",
};

export default function AdminAcordos() {
  const [candidatos, setCandidatos] = useState({ total_candidatos: 0, lojistas: [] });
  const [acordos, setAcordos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openLojista, setOpenLojista] = useState(null);
  const [modalCriar, setModalCriar] = useState(null); // { lojista, boletos_selecionados[], numero_parcelas, primeiro_vencimento }
  const [detalhe, setDetalhe] = useState(null);
  const [modalManual, setModalManual] = useState(null); // acordo avulso
  const [contratos, setContratos] = useState([]);
  const [cancelamento, setCancelamento] = useState(null); // { acordo_id, motivo, submitting }

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/acordos/candidatos"),
        api.get("/acordos"),
      ]);
      setCandidatos(c.data);
      setAcordos(a.data || []);
    } catch (e) {
      console.error("[AdminAcordos load]", e);
      toast.error(extractError(e) || "Falha ao carregar acordos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const abrirCriacaoManual = async () => {
    try {
      if (contratos.length === 0) {
        const { data } = await api.get("/contratos");
        setContratos((data || []).filter((c) => c.status === "ATIVO"));
      }
      const primeiro = new Date();
      primeiro.setDate(primeiro.getDate() + 7);
      setModalManual({
        contrato_id: "",
        valor_total: "",
        numero_parcelas: 3,
        valor_parcela_desejado: "",
        modo: "PARCELAS", // "PARCELAS" (define nº parcelas) | "VALOR_PARCELA" (calcula nº a partir do valor)
        periodicidade: "MENSAL",
        primeiro_vencimento: primeiro.toISOString().slice(0, 10),
        observacoes: "",
      });
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar lojistas");
    }
  };

  const submeterManual = async () => {
    const p = modalManual;
    if (!p.contrato_id) return toast.error("Selecione o lojista");
    if (!p.valor_total || Number(p.valor_total) <= 0) return toast.error("Informe o valor total");
    if (!p.primeiro_vencimento) return toast.error("Informe a data da 1ª parcela");

    let numero_parcelas = Number(p.numero_parcelas);
    if (p.modo === "VALOR_PARCELA") {
      const alvo = Number(p.valor_parcela_desejado);
      if (!alvo || alvo <= 0) return toast.error("Informe o valor desejado da parcela");
      numero_parcelas = Math.ceil(Number(p.valor_total) / alvo);
    }
    if (!numero_parcelas || numero_parcelas < 1) return toast.error("Quantidade de parcelas inválida");
    if (numero_parcelas > 60) return toast.error("Máximo de 60 parcelas");

    try {
      const { data } = await api.post("/acordos/manual", {
        contrato_id: p.contrato_id,
        valor_total: Number(p.valor_total),
        numero_parcelas,
        periodicidade: p.periodicidade,
        primeiro_vencimento: p.primeiro_vencimento,
        observacoes: p.observacoes || null,
      });
      toast.success(`Acordo avulso criado: ${data.numero_parcelas}x ${p.periodicidade.toLowerCase()} de R$ ${data.valor_parcela_padrao.toFixed(2)}`);
      setModalManual(null);
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao criar acordo manual");
    }
  };
  const abrirCriacao = (lojista) => {
    const primeiro = new Date();
    primeiro.setDate(primeiro.getDate() + 15);
    setModalCriar({
      lojista,
      boletos_selecionados: lojista.boletos.map((b) => b.id),
      numero_parcelas: Math.min(6, lojista.boletos.length),
      primeiro_vencimento: primeiro.toISOString().slice(0, 10),
      observacoes: "",
    });
  };

  const criar = async () => {
    if (!modalCriar) return;
    if (modalCriar.boletos_selecionados.length === 0) {
      toast.error("Selecione ao menos um boleto");
      return;
    }
    try {
      const { data } = await api.post("/acordos", {
        boletos_ids: modalCriar.boletos_selecionados,
        numero_parcelas: modalCriar.numero_parcelas,
        primeiro_vencimento: modalCriar.primeiro_vencimento,
        observacoes: modalCriar.observacoes || null,
      });
      toast.success(
        `Acordo criado: ${data.numero_parcelas}x R$ ${data.valor_parcela_padrao.toFixed(2)} (última ${data.valor_ultima_parcela.toFixed(2)})`
      );
      setModalCriar(null);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao criar acordo");
    }
  };

  const abrirDetalhe = async (id) => {
    try {
      const { data } = await api.get(`/acordos/${id}`);
      setDetalhe(data);
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar detalhes");
    }
  };

  const abrirCancelamento = (id) => {
    setCancelamento({ acordo_id: id, motivo: "", submitting: false });
  };

  const confirmarCancelamento = async () => {
    if (!cancelamento) return;
    const motivo = (cancelamento.motivo || "").trim();
    if (motivo.length < 3) {
      toast.error("Informe um motivo com pelo menos 3 caracteres");
      return;
    }
    setCancelamento({ ...cancelamento, submitting: true });
    try {
      const { data } = await api.post(`/acordos/${cancelamento.acordo_id}/cancelar`, { motivo });
      toast.success(
        `Acordo cancelado. ${data.parcelas_canceladas || 0} parcela(s) cancelada(s)` +
          (data.boletos_originais_reabertos ? `, ${data.boletos_originais_reabertos} boleto(s) reaberto(s).` : ".")
      );
      setCancelamento(null);
      setDetalhe(null);
      await load();
    } catch (e) {
      console.error("[cancelar acordo]", e);
      toast.error(extractError(e) || "Falha ao cancelar acordo");
      setCancelamento({ ...cancelamento, submitting: false });
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-start gap-3 flex-wrap">
        <Handshake className="text-primary mt-1" />
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-bold" data-testid="acordos-title">Acordos de Parcelamento</h1>
          <p className="text-sm text-muted-foreground">
            Dívidas &gt; 60 dias podem ser divididas em até 6 parcelas mensais mediante autorização. Use "Novo Acordo" para casos avulsos.
          </p>
        </div>
        <Button
          data-testid="acordos-btn-novo-manual"
          onClick={abrirCriacaoManual}
          className="rounded-sm gap-2"
        >
          <Handshake size={16} /> + Novo Acordo
        </Button>
      </div>

      {/* Candidatos ao acordo (lojistas com dívida > 60d) */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="text-orange-500" size={18} />
          <h2 className="font-semibold">Elegíveis para acordo · {candidatos.total_candidatos} boletos em atraso &gt; 60 dias</h2>
        </div>
        {candidatos.lojistas.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Nenhum lojista com dívida elegível no momento. ✅
          </div>
        ) : (
          <div className="space-y-2">
            {candidatos.lojistas.map((l) => (
              <div key={l.contrato_id} className="border rounded-lg p-3" data-testid={`candidato-${l.contrato_id}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <div className="font-semibold">{l.razao_social}</div>
                    <div className="text-xs text-muted-foreground">
                      {l.boletos.length} boleto(s) · maior atraso {l.maior_atraso} dias
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-bold text-orange-600">{fmtBRL(l.divida_total)}</div>
                    <Button
                      data-testid={`btn-criar-acordo-${l.contrato_id}`}
                      onClick={() => abrirCriacao(l)}
                      size="sm"
                    >
                      Criar Acordo
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpenLojista(openLojista === l.contrato_id ? null : l.contrato_id)}
                    >
                      {openLojista === l.contrato_id ? "Ocultar" : "Ver boletos"}
                    </Button>
                  </div>
                </div>
                {openLojista === l.contrato_id && (
                  <div className="mt-3 border-t pt-3 space-y-1">
                    {l.boletos.map((b) => (
                      <div key={b.id} className="flex justify-between text-sm">
                        <span>{b.competencia} · venc {fmtDate(b.vencimento)} · {b.dias_atraso} dias atraso</span>
                        <span className="font-semibold">{fmtBRL(b.valor_total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Acordos ativos */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Handshake size={18} className="text-blue-500" />
          <h2 className="font-semibold">Acordos ({acordos.length})</h2>
        </div>
        {acordos.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nenhum acordo cadastrado ainda.</div>
        ) : (
          <div className="space-y-2">
            {acordos.map((a) => (
              <div
                key={a.id}
                onClick={() => abrirDetalhe(a.id)}
                className="border rounded-lg p-3 cursor-pointer hover:bg-muted/40 transition"
                data-testid={`acordo-${a.id}`}
              >
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <div className="font-semibold">{a.razao_social}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.numero_parcelas} parcelas · 1º venc {fmtDate(a.primeiro_vencimento)} · autorizado por {a.autorizado_por}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold">{fmtBRL(a.valor_total_divida)}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.parcelas_pagas}/{a.numero_parcelas} pagas
                      </div>
                    </div>
                    <Badge className={STATUS_COLOR[a.status] || ""}>{a.status}</Badge>
                  </div>
                </div>
                {/* Barra de progresso */}
                <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${(a.parcelas_pagas / a.numero_parcelas) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal: criar acordo */}
      <Dialog open={!!modalCriar} onOpenChange={(o) => !o && setModalCriar(null)}>
        <DialogContent className="max-w-md" data-testid="modal-criar-acordo">
          <DialogHeader>
            <DialogTitle>Criar Acordo — {modalCriar?.lojista?.razao_social}</DialogTitle>
          </DialogHeader>
          {modalCriar && (
            <div className="space-y-3">
              <div className="p-3 bg-muted/40 rounded-lg text-sm space-y-1">
                <div>Dívida total: <span className="font-bold">{fmtBRL(modalCriar.lojista.divida_total)}</span></div>
                <div>Boletos: {modalCriar.lojista.boletos.length} · maior atraso {modalCriar.lojista.maior_atraso}d</div>
              </div>
              <div>
                <Label htmlFor="parcelas">Quantidade de parcelas (1-6)</Label>
                <Input
                  id="parcelas" type="number" min={1} max={6}
                  value={modalCriar.numero_parcelas}
                  onChange={(e) => setModalCriar({ ...modalCriar, numero_parcelas: parseInt(e.target.value || "1", 10) })}
                  data-testid="input-num-parcelas"
                />
                {modalCriar.numero_parcelas > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Valor por parcela: {fmtBRL(modalCriar.lojista.divida_total / modalCriar.numero_parcelas)}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="primeiro">Vencimento da 1ª parcela</Label>
                <Input
                  id="primeiro" type="date"
                  value={modalCriar.primeiro_vencimento}
                  onChange={(e) => setModalCriar({ ...modalCriar, primeiro_vencimento: e.target.value })}
                  data-testid="input-primeiro-venc"
                />
              </div>
              <div>
                <Label htmlFor="obs">Observações (opcional)</Label>
                <Input
                  id="obs" value={modalCriar.observacoes}
                  onChange={(e) => setModalCriar({ ...modalCriar, observacoes: e.target.value })}
                  placeholder="Ex: acordo verbal em reunião, prazo especial..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalCriar(null)}>Cancelar</Button>
            <Button onClick={criar} data-testid="btn-confirmar-acordo">Autorizar e Gerar Parcelas</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: detalhe do acordo */}
      <Dialog open={!!detalhe} onOpenChange={(o) => !o && setDetalhe(null)}>
        <DialogContent className="max-w-lg" data-testid="modal-detalhe-acordo">
          <DialogHeader>
            <DialogTitle>Acordo — {detalhe?.razao_social}</DialogTitle>
          </DialogHeader>
          {detalhe && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/40 rounded-lg text-sm">
                <div>Total: <span className="font-bold">{fmtBRL(detalhe.valor_total_divida)}</span> em {detalhe.numero_parcelas}x</div>
                <div>Status: <Badge className={STATUS_COLOR[detalhe.status]}>{detalhe.status}</Badge></div>
                {detalhe.observacoes && <div className="mt-1 italic text-xs">"{detalhe.observacoes}"</div>}
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Parcelas ({detalhe.parcelas.length})</div>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {detalhe.parcelas.map((p) => (
                    <div key={p.numero} className="flex items-center justify-between text-sm border-b py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-6 text-center">{p.numero}</span>
                        <Calendar size={12} /> {fmtDate(p.vencimento)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span>{fmtBRL(p.valor)}</span>
                        {p.status === "PAGO" ? (
                          <CheckCircle2 size={14} className="text-emerald-500" />
                        ) : p.status === "CANCELADO" ? (
                          <XCircle size={14} className="text-red-500" />
                        ) : (
                          <Badge variant="outline" className="text-xs">{p.status}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {detalhe.boletos_origem && (
                <div>
                  <div className="text-xs text-muted-foreground">
                    {detalhe.boletos_origem.length} boleto(s) originais consolidados neste acordo.
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detalhe && !["QUITADO", "CANCELADO"].includes(detalhe.status) && (
              <Button variant="destructive" onClick={() => abrirCancelamento(detalhe.id)} data-testid="btn-cancelar-acordo">
                Cancelar Acordo
              </Button>
            )}
            <Button variant="ghost" onClick={() => setDetalhe(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal — Novo Acordo Manual/Avulso */}
      <Dialog open={!!modalManual} onOpenChange={(o) => !o && setModalManual(null)}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="acordos-modal-manual">
          <DialogHeader>
            <DialogTitle>Novo Acordo Avulso</DialogTitle>
          </DialogHeader>
          {modalManual && (
            <div className="grid gap-3 py-2 text-sm">
              <div className="grid gap-1">
                <Label>Lojista</Label>
                <select
                  data-testid="manual-contrato-select"
                  className="border rounded-sm px-2 py-2 bg-background text-sm"
                  value={modalManual.contrato_id}
                  onChange={(e) => setModalManual({ ...modalManual, contrato_id: e.target.value })}
                >
                  <option value="">— selecione —</option>
                  {contratos.map((c) => (
                    <option key={c.id} value={c.id}>{c.razao_social} · {c.cnpj}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1">
                <Label>Valor total negociado (R$)</Label>
                <Input
                  data-testid="manual-valor-total"
                  type="number" step="0.01" min="0.01"
                  value={modalManual.valor_total}
                  onChange={(e) => setModalManual({ ...modalManual, valor_total: e.target.value })}
                  placeholder="Ex.: 3500.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1">
                  <Label>Periodicidade</Label>
                  <select
                    data-testid="manual-periodicidade"
                    className="border rounded-sm px-2 py-2 bg-background text-sm"
                    value={modalManual.periodicidade}
                    onChange={(e) => setModalManual({ ...modalManual, periodicidade: e.target.value })}
                  >
                    <option value="SEMANAL">Semanal</option>
                    <option value="QUINZENAL">Quinzenal</option>
                    <option value="MENSAL">Mensal</option>
                  </select>
                </div>
                <div className="grid gap-1">
                  <Label>Calcular por</Label>
                  <select
                    data-testid="manual-modo"
                    className="border rounded-sm px-2 py-2 bg-background text-sm"
                    value={modalManual.modo}
                    onChange={(e) => setModalManual({ ...modalManual, modo: e.target.value })}
                  >
                    <option value="PARCELAS">Nº de parcelas</option>
                    <option value="VALOR_PARCELA">Valor desejado por parcela</option>
                  </select>
                </div>
              </div>
              {modalManual.modo === "PARCELAS" ? (
                <div className="grid gap-1">
                  <Label>Quantidade de parcelas</Label>
                  <Input
                    data-testid="manual-num-parcelas"
                    type="number" min="1" max="60"
                    value={modalManual.numero_parcelas}
                    onChange={(e) => setModalManual({ ...modalManual, numero_parcelas: e.target.value })}
                  />
                </div>
              ) : (
                <div className="grid gap-1">
                  <Label>Valor desejado da parcela (R$)</Label>
                  <Input
                    data-testid="manual-valor-parcela-desejado"
                    type="number" step="0.01" min="0.01"
                    value={modalManual.valor_parcela_desejado}
                    onChange={(e) => setModalManual({ ...modalManual, valor_parcela_desejado: e.target.value })}
                    placeholder="Ex.: 1000.00"
                  />
                  {(() => {
                    const total = Number(modalManual.valor_total);
                    const alvo = Number(modalManual.valor_parcela_desejado);
                    if (total > 0 && alvo > 0) {
                      const n = Math.ceil(total / alvo);
                      const ultima = Math.round((total - alvo * (n - 1)) * 100) / 100;
                      return (
                        <div className="text-xs text-muted-foreground mt-1" data-testid="manual-reverse-math-preview">
                          → Serão geradas <strong>{n}</strong> parcelas ({n - 1}× {fmtBRL(alvo)} + última de {fmtBRL(ultima)})
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              <div className="grid gap-1">
                <Label>Vencimento da 1ª parcela (ou parcela atual em andamento)</Label>
                <Input
                  data-testid="manual-primeiro-venc"
                  type="date"
                  value={modalManual.primeiro_vencimento}
                  onChange={(e) => setModalManual({ ...modalManual, primeiro_vencimento: e.target.value })}
                />
              </div>
              <div className="grid gap-1">
                <Label>Observações</Label>
                <textarea
                  data-testid="manual-observacoes"
                  className="border rounded-sm px-2 py-2 bg-background text-sm min-h-[70px]"
                  value={modalManual.observacoes}
                  onChange={(e) => setModalManual({ ...modalManual, observacoes: e.target.value })}
                  placeholder="Ex.: refinanciamento dívida anterior à implantação"
                />
              </div>
              {modalManual.modo === "PARCELAS" && modalManual.valor_total && modalManual.numero_parcelas > 0 && (
                <div className="text-xs bg-muted rounded-sm p-2">
                  Vai gerar <strong>{modalManual.numero_parcelas}</strong> parcelas
                  {" "}<strong>{modalManual.periodicidade.toLowerCase()}</strong> de aproximadamente
                  {" "}<strong>{fmtBRL(Number(modalManual.valor_total) / modalManual.numero_parcelas)}</strong>
                  {" "}(a última ajustada para bater o total exato).
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalManual(null)}>Cancelar</Button>
            <Button data-testid="manual-submeter" onClick={submeterManual}>Criar Acordo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal — Cancelar Acordo (motivo) */}
      <Dialog open={!!cancelamento} onOpenChange={(o) => !o && !cancelamento?.submitting && setCancelamento(null)}>
        <DialogContent className="max-w-md" data-testid="modal-cancelar-acordo">
          <DialogHeader>
            <DialogTitle>Cancelar Acordo</DialogTitle>
          </DialogHeader>
          {cancelamento && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ao cancelar, as parcelas em aberto serão marcadas como <strong>CANCELADO</strong>.
                Boletos originais (se houver) voltam para <strong>VENCIDO</strong>. Parcelas já pagas não são afetadas.
              </p>
              <div className="grid gap-1">
                <Label>Motivo do cancelamento (mín. 3 caracteres)</Label>
                <textarea
                  data-testid="cancelamento-motivo"
                  className="border rounded-sm px-2 py-2 bg-background text-sm min-h-[80px]"
                  value={cancelamento.motivo}
                  onChange={(e) => setCancelamento({ ...cancelamento, motivo: e.target.value })}
                  placeholder="Ex.: renegociação, erro de lançamento, quitação à vista..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" disabled={cancelamento?.submitting} onClick={() => setCancelamento(null)}>Voltar</Button>
            <Button
              variant="destructive"
              disabled={cancelamento?.submitting || (cancelamento?.motivo || "").trim().length < 3}
              onClick={confirmarCancelamento}
              data-testid="btn-confirmar-cancelamento"
            >
              {cancelamento?.submitting ? "Cancelando..." : "Confirmar Cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
